import { useCallback, useMemo, useState } from 'react'
import type { GridApi, GridColumnState } from '@/components/grid/types'
import {
  COLUMN_CONFIG,
  COLUMN_VISIBILITY_KEY,
  BASIC_LIST_FIELDS,
  PRESET_ALWAYS_FIELDS,
} from './column-config'
import { useSavedViewsContext } from '@/features/config/contexts/saved-views-context'

export function useColumnVisibility(
  apiRef: React.MutableRefObject<GridApi<any> | null>,
) {
  const { clearActiveView } = useSavedViewsContext()
  const [columnVisibilityAnchor, setColumnVisibilityAnchor] =
    useState<HTMLButtonElement | null>(null)
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    () => {
      const saved = localStorage.getItem(COLUMN_VISIBILITY_KEY)
      if (saved) {
        try {
          return JSON.parse(saved)
        } catch {
          return {}
        }
      }
      return {}
    },
  )

  const applyVisibility = useCallback(
    (newVisibility: Record<string, boolean>) => {
      setVisibleColumns(newVisibility)
      localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(newVisibility))

      const api = apiRef.current
      if (api) {
        COLUMN_CONFIG.forEach((col) => {
          const isVisible =
            newVisibility[col.field] !== undefined
              ? newVisibility[col.field]
              : true
          api.setColumnsVisible([col.field], isVisible)
        })
      }
    },
    [apiRef],
  )

  /**
   * Restores a saved view. Order and width go through the grid, which owns
   * them; visibility goes through `applyVisibility` as well so the popover's
   * checkboxes and the stored preference agree with what the grid is drawing.
   */
  const applySavedView = useCallback(
    (state: GridColumnState[]) => {
      const visibility: Record<string, boolean> = {}
      COLUMN_CONFIG.forEach((col) => {
        visibility[col.field] = true
      })
      state.forEach((entry) => {
        visibility[entry.colId] = !entry.hide
      })

      applyVisibility(visibility)
      apiRef.current?.applyColumnState(state, { applyVisibility: false })
    },
    [applyVisibility, apiRef],
  )

  const handleOpenColumnVisibility = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setColumnVisibilityAnchor(event.currentTarget)
    },
    [],
  )

  const handleCloseColumnVisibility = useCallback(() => {
    setColumnVisibilityAnchor(null)
  }, [])

  const handleToggleColumn = useCallback(
    (field: string) => {
      const currentVisibility =
        visibleColumns[field] !== undefined ? visibleColumns[field] : true

      const newVisibility = {
        ...visibleColumns,
        [field]: !currentVisibility,
      }
      applyVisibility(newVisibility)
      clearActiveView()
    },
    [visibleColumns, applyVisibility, clearActiveView],
  )

  const handleShowAllColumns = useCallback(() => {
    const allVisible: Record<string, boolean> = {}
    COLUMN_CONFIG.forEach((col) => {
      allVisible[col.field] = true
    })
    applyVisibility(allVisible)
    clearActiveView()
  }, [applyVisibility, clearActiveView])

  const handleHideAllColumns = useCallback(() => {
    const allHidden: Record<string, boolean> = {}
    COLUMN_CONFIG.forEach((col: any) => {
      if (
        col.field === 'id' ||
        col.field === 'viewAction' ||
        col.field === 'description'
      ) {
        allHidden[col.field] = true
      } else {
        allHidden[col.field] = false
      }
    })
    applyVisibility(allHidden)
    clearActiveView()
  }, [applyVisibility, clearActiveView])

  const handleApplyPreset = useCallback(
    (fields: string[]) => {
      const show = new Set([...PRESET_ALWAYS_FIELDS, ...fields])
      const next: Record<string, boolean> = {}
      COLUMN_CONFIG.forEach((col) => {
        next[col.field] = show.has(col.field)
      })
      applyVisibility(next)
      clearActiveView()
    },
    [applyVisibility, clearActiveView],
  )

  const isBasicListActive = useMemo(() => {
    return COLUMN_CONFIG.every((col) => {
      const shouldBeVisible = BASIC_LIST_FIELDS.includes(col.field)
      const isVisible = visibleColumns[col.field] !== false
      return shouldBeVisible === isVisible
    })
  }, [visibleColumns])

  const handleBasicListView = useCallback(() => {
    if (isBasicListActive) {
      const allVisible: Record<string, boolean> = {}
      COLUMN_CONFIG.forEach((col) => {
        allVisible[col.field] = true
      })
      applyVisibility(allVisible)
    } else {
      const basicVisibility: Record<string, boolean> = {}
      COLUMN_CONFIG.forEach((col) => {
        basicVisibility[col.field] = BASIC_LIST_FIELDS.includes(col.field)
      })
      applyVisibility(basicVisibility)
    }
    clearActiveView()
  }, [applyVisibility, isBasicListActive, clearActiveView])

  return {
    applySavedView,
    columnVisibilityAnchor,
    visibleColumns,
    isBasicListActive,
    handleOpenColumnVisibility,
    handleCloseColumnVisibility,
    handleToggleColumn,
    handleShowAllColumns,
    handleHideAllColumns,
    handleBasicListView,
    handleApplyPreset,
  }
}
